import React, { useMemo, useState, useEffect } from "react";
import {
  SequenceRenderCfg,
  WeatherKey,
} from "./IslandRegistry";

type Props = {
  data: { date: Date; score: number }[];
  renderCfg: SequenceRenderCfg;
};

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfWeek(d: Date) {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - dow);
  return x;
}

function endOfWeek(d: Date) {
  const x = startOfWeek(d);
  x.setDate(x.getDate() + 6);
  return x;
}

const EvolutionCalendar: React.FC<Props> = ({ data, renderCfg }) => {
  const [monthIndex, setMonthIndex] = useState(0);

  const isEmojiMode =
    renderCfg.chartType === "emojis" &&
    renderCfg.scale?.mode === "emoji";

  const isSpriteMode =
    renderCfg.chartType === "sprites" &&
    renderCfg.scale?.mode === "sprite" &&
    !!renderCfg.spriteUrls;

  const hasCalendarVisual = isEmojiMode || isSpriteMode;

  // Group data by month
  const months = useMemo(() => {
    if (!data.length) return [];
    const byMonth = new Map<string, { date: Date; score: number }[]>();

    for (const d of data) {
      const ym = `${d.date.getFullYear()}-${d.date.getMonth()}`;
      if (!byMonth.has(ym)) byMonth.set(ym, []);
      byMonth.get(ym)!.push(d);
    }

    return Array.from(byMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ym, arr]) => {
        const [y, m] = ym.split("-").map(Number);
        const firstOfMonth = new Date(y, m, 1);
        const lastOfMonth = new Date(y, m + 1, 0);
        const gridStart = startOfWeek(firstOfMonth);
        const gridEnd = endOfWeek(lastOfMonth);

        const days: Date[] = [];
        for (let d = new Date(gridStart); d <= gridEnd; d.setDate(d.getDate() + 1)) {
          days.push(new Date(d));
        }

        const weeks: Date[][] = [];
        for (let i = 0; i < days.length; i += 7) {
          weeks.push(days.slice(i, i + 7));
        }

        return {
          monthLabel: firstOfMonth.toLocaleString(undefined, {
            month: "long",
            year: "numeric",
          }),
          firstOfMonth,
          weeks,
        };
      });
  }, [data]);

  // 👉 Automatically start at the latest month
  useEffect(() => {
    if (months.length > 0) {
      setMonthIndex(months.length - 1);
    }
  }, [months]);

  if (!months.length) {
    return (
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        No data available.
      </div>
    );
  }

  const current = months[monthIndex];

  // Map daily scores → emoji or sprite URL (latest score of each day wins)
  const valueByDayKey = useMemo(() => {
    const m = new Map<string, string>();
    const scale = renderCfg.scale;
    if (!scale) return m;

    const sorted = [...data].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );

    for (const d of sorted) {
      const dayKey = startOfDay(d.date).toISOString().slice(0, 10);
      const mappedKey = scale.map(d.score); // emoji char OR sprite key

      if (isEmojiMode) {
        m.set(dayKey, mappedKey); // emoji text
      } else if (isSpriteMode && renderCfg.spriteUrls) {
        const url = renderCfg.spriteUrls[mappedKey as WeatherKey];
        if (url) m.set(dayKey, url); // sprite URL
      }
    }

    return m;
  }, [data, renderCfg.scale, renderCfg.spriteUrls, isEmojiMode, isSpriteMode]);

  // Add this to navigate between months
  const goPrevMonth = () => setMonthIndex((i) => Math.max(0, i - 1));
  const goNextMonth = () => setMonthIndex((i) => Math.min(months.length - 1, i + 1));

  return (
    <div key={current.monthLabel} style={{ maxWidth: 640, margin: "20px" }}>
      <style>{`
        .cal-header {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }
        .cal-arrow {
          border: none;
          background: transparent;
          font-size: 1.5rem;
          cursor: pointer;
          opacity: 0.6;
          transition: opacity 0.2s;
        }
        .cal-arrow:disabled {
          opacity: 0.2;
          cursor: default;
        }
        .cal-title {
          font-weight: 600;
          text-transform: capitalize;
        }
        .cal-grid {
          display:grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }
        .cal-weekday {
          font-size: 12px;
          opacity: .7;
          text-align:center;
        }
        .cal-cell {
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 8px;
          aspect-ratio: 1 / 1;
          display:flex;
          flex-direction:column;
          justify-content:center;
          align-items:center;
          padding: 0px 0px;
        }
        .cal-day { font-size: 12px; opacity: .8; }
        .cal-emoji {
          font-size: 20px;
          line-height: 1.1;
          margin-top: 4px;
          opacity: 0;
          transform: translateY(-8px);
          animation: calIconDrop 0.6s ease-out forwards;
        }
        .cal-sprite {
          margin-top: 4px;
          opacity: 0;
          transform: translateY(-8px);
          animation: calIconDrop 0.6s ease-out forwards;
          display:flex;
          justify-content:center;
          align-items:center;
        }
        .cal-sprite img {
          width: 26px;
          height: 26px;
          object-fit: contain;
        }
        .cal-faded { opacity: .4; }

        @keyframes calIconDrop {
          0% { opacity: 0; transform: translateY(-10px) scale(0.8); }
          70% { opacity: 1; transform: translateY(2px) scale(1.05); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Month navigation */}
      <div className="cal-header">
        <button className="cal-arrow" onClick={goPrevMonth} disabled={monthIndex === 0}>
          ‹
        </button>
        <div className="cal-title">{current.monthLabel}</div>
        <button
          className="cal-arrow"
          onClick={goNextMonth}
          disabled={monthIndex === months.length - 1}
        >
          ›
        </button>
      </div>

      {/* Weekday headers */}
      <div className="cal-grid" style={{ marginBottom: 6 }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => (
          <div key={w} className="cal-weekday">{w}</div>
        ))}
      </div>

      {/* Weeks */}
      {current.weeks.map((week, weekIdx) => (
        <div key={weekIdx} className="cal-grid">
          {week.map((d) => {
            const key = d.toISOString().slice(0, 10);
            const value = valueByDayKey.get(key); // emoji or URL
            const inMonth = d.getMonth() === current.firstOfMonth.getMonth();

            // for stagger animation
            const flatIndex = weekIdx * 7 + ((d.getDay() + 6) % 7);

            return (
              <div key={key} className={`cal-cell ${inMonth ? "" : "cal-faded"}`}>
                <div className="cal-day">{d.getDate()}</div>

                {isEmojiMode && (
                  <div
                    className="cal-emoji"
                    style={{
                      animationDelay: value ? `${flatIndex * 0.05}s` : "0s",
                    }}
                  >
                    {value ?? ""}
                  </div>
                )}

                {isSpriteMode && (
                  <div
                    className="cal-sprite"
                    style={{
                      animationDelay: value ? `${flatIndex * 0.05}s` : "0s",
                    }}
                  >
                    {value && <img src={value} alt="" />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {!hasCalendarVisual && (
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
          Calendar view requires an emoji or sprite scale for this sequence.
        </div>
      )}
    </div>
  );
};

export default React.memo(EvolutionCalendar);
